"use client";

import Image from "next/image";
import { useEffect, useState, type ReactNode } from "react";
import { useReducedMotion } from "framer-motion";
import { ZoomParallax } from "@/components/motion/ZoomParallax";
import type { ProgramSummary } from "@/components/programs/ProgramsDarkView";

interface ProgramsParallaxHeroProps {
  programs: ProgramSummary[];
}

function toHeroImage(program: ProgramSummary) {
  return { src: program.image, alt: program.title };
}

function ProgramsHeroOverlay() {
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black/75 via-black/40 to-transparent px-4 pb-10 pt-16 md:px-6 md:pb-14">
      <div className="mx-auto max-w-7xl">
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--charity-gold)]">
          Our Programs
        </p>
        <h1 className="font-display max-w-2xl text-3xl font-bold leading-tight text-white sm:text-4xl md:text-5xl">
          Making a Difference Together
        </h1>
        <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/90 sm:text-base">
          Comprehensive initiatives addressing community needs across multiple
          sectors.
        </p>
      </div>
    </div>
  );
}

function useParallaxScrollHeight() {
  const [height, setHeight] = useState("150vh");

  useEffect(() => {
    const update = () => {
      setHeight(window.innerWidth < 768 ? "110vh" : "150vh");
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return height;
}

function ProgramsParallaxHeroStatic({ programs }: ProgramsParallaxHeroProps) {
  const featured = programs[0];

  if (!featured) return null;

  return (
    <section className="relative min-h-[min(72vh,640px)] overflow-hidden pt-[var(--header-offset)] md:min-h-[calc(100vh-var(--header-offset))]">
      <div className="absolute inset-0">
        <Image
          src={featured.image}
          alt=""
          fill
          priority
          className="object-cover"
          sizes="100vw"
          aria-hidden
        />
        <div
          className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20"
          aria-hidden
        />
      </div>
      <ProgramsHeroOverlay />
    </section>
  );
}

export function ProgramsParallaxHero({ programs }: ProgramsParallaxHeroProps) {
  const reduced = useReducedMotion();
  const scrollHeight = useParallaxScrollHeight();

  if (reduced || programs.length === 0) {
    return <ProgramsParallaxHeroStatic programs={programs} />;
  }

  const images = programs.slice(0, 7).map(toHeroImage);
  const overlay: ReactNode = <ProgramsHeroOverlay />;

  return (
    <section className="relative" aria-label="Programs hero">
      <ZoomParallax
        images={images}
        scrollHeight={scrollHeight}
        overlay={overlay}
      />
    </section>
  );
}
